import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CartModificationList } from '@spartacus/cart/base/root';
import {
  DynamicAttributes,
  GlobalMessageService,
  GlobalMessageType,
  OccEndpointsService,
  Product,
  UserIdService,
} from '@spartacus/core';
import { Order } from '@spartacus/order/root';
import { BehaviorSubject, Observable, combineLatest, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import {
  DocumentDetails,
  GroupCheckoutCustomerBaseType,
  GroupCheckoutRulesProductType,
  GroupCheckoutState,
  GroupCheckoutStep,
  GroupCheckoutStepData,
  GroupCheckoutType,
} from '../features/checkout-group-sales/model/group-checkout-step.model';
import { UdxB2BdraftOrderList } from '../features/model/udxb2b-draft-order.model';
import {
  GroupSalesAddonMealProduct,
  GroupSalesAddonProduct,
  GroupSalesMainVariantProduct,
  GroupSalesOrderHistory,
  MediaData,
  UdxB2BGroupSalesPLP,
  UdxGroupRuleAddToCartRequest,
} from '../features/model/udxb2b-group-sales.model';
import { ProgressSpinnerDialogComponent } from '../utilities/spinner/progress-spinner-dialog.component';
import { UdxDataTransferService } from './udx-data-transfer.service.service';
import { translationsCs as translations } from '@spartacus/assets';
import { AdditionalOrderPOSTPayload } from '../features/model';
import { UDXGroupSalesRefundData } from '../features/group-sales/group-sales-orders-refund/model/group-sales-order-refund-model';

@Injectable({
  providedIn: 'root',
})
export class UdxGroupSalesService {
  // initial enabled steps
  allSteps: GroupCheckoutStep[] = [
    {
      id: 'groupRuleDetails',
      name: 'common.groupRulesCheckoutTitle',
      state: GroupCheckoutState.eStateGroupDetails,
    },
    {
      id: 'groupPaymentType',
      name: 'checkoutB2B.progress.methodOfPayment',
      state: GroupCheckoutState.eStatePaymentType,
    },
    {
      id: 'groupAnnualInformation',
      name: 'checkoutB2B.progress.vistorInfo',
      state: GroupCheckoutState.estateAnnualInformation,
    },
    {
      id: 'groupDeliveryMode',
      name: 'checkoutProgress.deliveryMode',
      state: GroupCheckoutState.eStateDeliveryMode,
    },
    {
      id: 'groupReviewOrder',
      name: 'checkoutProgress.reviewOrder',
      state: GroupCheckoutState.eReviewOrder,
    },
  ];
  filteredSteps: GroupCheckoutStep[] = this.allSteps; 
  readonly steps$: BehaviorSubject<GroupCheckoutStep[]> = new BehaviorSubject<GroupCheckoutStep[]>([]);
  constructor(
    protected _http: HttpClient,
    protected _occEndpoints: OccEndpointsService,
    protected dataTransfer: UdxDataTransferService,
    protected globalMessage: GlobalMessageService,
    protected userIdService: UserIdService,
    public dialog: MatDialog
  ) {
    this.resetSteps();
  }

  protected GLOBAL_MESSAGE_TIMEOUT = 10000;
  //API implementation for the group sale plp product fetch and filters - GET
  getGroupSalesProductList(
    currentPage: number,
    pageSize: number,
    ruleOrderTypes: string,
    productTypes: string,
    customerBases: string,
    minQty: string
  ) {
    let queryParams = new HttpParams()
      .set('ruleOrderType', ruleOrderTypes)
      .set('rulesProductType', productTypes)
      .set('customerBase', customerBases)
      .set('currentPage', currentPage)
      .set('pageSize', pageSize);
    if (minQty != '' && minQty != null) {
      queryParams = queryParams.append('minQty', minQty);
    }
    return this._http.get<UdxB2BGroupSalesPLP>(this._occEndpoints.buildUrl('getGroupSalesProductList'), {
      params: queryParams,
    });
  }

  getDraftOrders(
    sortBy: string,
    orderId: string,
    groupName: string,
    size: string,
    creationFrom: string,
    creationTo: string,
    updateFrom: string,
    updateTo: string,
    reservationFrom: string,
    reservationTo: string,
    productType: string,
    currentPage: string,
    pageSize: string
  ) {
    let queryParams = new HttpParams();
    if (sortBy != '' && sortBy != null) {
      queryParams = queryParams.append('sortBy', sortBy);
    }
    if (orderId != '' && orderId != null) {
      queryParams = queryParams.append('orderId', orderId);
    }
    if (groupName != '' && groupName != null) {
      queryParams = queryParams.append('groupName', groupName);
    }
    if (size != '' && size != null) {
      queryParams = queryParams.append('size', size);
    }
    if (creationFrom != '' && creationFrom != null) {
      queryParams = queryParams.append('creationFrom', creationFrom);
    }
    if (creationTo != '' && creationTo != null) {
      queryParams = queryParams.append('creationTo', creationTo);
    }
    if (updateFrom != '' && updateFrom != null) {
      queryParams = queryParams.append('updateFrom', updateFrom);
    }
    if (updateTo != '' && updateTo != null) {
      queryParams = queryParams.append('updateTo', updateTo);
    }
    if (reservationFrom != '' && reservationFrom != null) {
      queryParams = queryParams.append('reservationFrom', reservationFrom);
    }
    if (reservationTo != '' && reservationTo != null) {
      queryParams = queryParams.append('reservationTo', reservationTo);
    }
    if (productType != '' && productType != null) {
      queryParams = queryParams.append('productType', productType);
    }
    if (currentPage != '' && currentPage != null) {
      queryParams = queryParams.append('currentPage', currentPage);
    }
    if (pageSize != '' && pageSize != null) {
      queryParams = queryParams.append('pageSize', pageSize);
    }
    return this._http.get<UdxB2BdraftOrderList>(this._occEndpoints.buildUrl('getDraftOrderList'), {
      params: queryParams,
    });
  }

  deleteDraftOrder(orderId: string): Observable<any> {
    let queryParams = new HttpParams().set('orderId', orderId);
    return this._http.delete(this._occEndpoints.buildUrl('deleteDraftOrder'), {
      params: queryParams,
      observe: 'response',
    });
  }

  groupCartEdit(orderId: string) {
    return this._http.post(this._occEndpoints.buildUrl('groupCartEdit') + '?orderId=' + orderId, {});
  }
  addGroupRuleProductToCart(
    product: Product,
    visitationDate: string,
    orderIDString: string | null
  ): Observable<CartModificationList> {
    let mainVariantProductList: GroupSalesMainVariantProduct[] = [];
    let addonProductList: GroupSalesAddonProduct[] = [];
    let addonMealProductList: GroupSalesAddonMealProduct[] = [];

    let requestBody: UdxGroupRuleAddToCartRequest;
    // date selection is sent as false for groupbookings inactiveAP case
    // we dont send visitationDate in this case in the payload
    if (product?.dateSelectionRequired) {
      requestBody = {
        mainProduct: {
          productCode: product.mainProductCode,
        },
        mainVariantProducts: mainVariantProductList,
        addonProducts: addonProductList,
        addonMealProducts: addonMealProductList,
        visitationDate: visitationDate,
        subTotal: '',
        groupRuleId: product.code,
      };
    } else {
      requestBody = {
        mainProduct: {
          productCode: product.mainProductCode,
        },
        mainVariantProducts: mainVariantProductList,
        addonProducts: addonProductList,
        addonMealProducts: addonMealProductList,
        subTotal: '',
        groupRuleId: product.code,
      };
    }

    let quantityMap$ = this.dataTransfer.quantityData$;
    let subTotal$ = this.dataTransfer.groupRuleSubtotal$;
    let userId$ = this.userIdService.takeUserId();

    return combineLatest([quantityMap$, subTotal$, userId$]).pipe(
      take(1),
      map(([quantity, subtotal, userId]) => {
        requestBody.subTotal = subtotal.toString();
        // if (requestBody.visitationDate) {
        //   const [datePart, time, period] = requestBody.visitationDate.split(' ');
        //   const [year, month, day] = datePart.split('-');
        //   const timePart = `${time} ${period}`;  // Combine time and AM/PM part

        //   requestBody.visitationDate = `${month}/${day}/${year} ${timePart}`;
        // }
        quantity.forEach((value, key) => {
          if (key.includes('#mealAddon')) {
            let addonMealProduct: GroupSalesAddonMealProduct = {};
            addonMealProduct.productCode = key.split('#')[0];
            addonMealProduct.quantity = value.toString();
            addonMealProductList.push(addonMealProduct);
          } else if (key.includes('#addonProduct')) {
            let addonProduct: GroupSalesAddonProduct = {};
            addonProduct.productCode = key.split('#')[0];
            addonProduct.quantity = value.toString();
            addonProductList.push(addonProduct);
          } else {
            let mainProductVariant: GroupSalesMainVariantProduct = {};
            mainProductVariant.productCode = key;
            mainProductVariant.quantity = value.toString();
            mainVariantProductList.push(mainProductVariant);
          }
        });
        return userId;
      }),
      tap(() => console.log(' Group Rule Add to cart Request ', requestBody)),
      mergeMap((userId) => {
        var paramsObject: DynamicAttributes = {
          urlParams: {
            userId,
          },
        };
        if (orderIDString) {
          paramsObject.queryParams = {
            orderId: orderIDString,
          };
        }
        return this._http
          .post<CartModificationList>(
            this._occEndpoints.buildUrl('groupRuleAddToCart', paramsObject),
            requestBody,
            httpOptions
          )
          .pipe(catchError((error) => this.handleGroupRuleAddToCart(error)));
      })
    );
  }

  handleGroupRuleAddToCart(errorResponse: any): Observable<CartModificationList> {
    var params = null;
    if (errorResponse.error.errors) {
      params = { msg: errorResponse.error.errors[0].message };
    }
    let theKey = params ? `groupSalesPDP.httpHandlers.pdpAddToCart` : `groupSalesPDP.httpHandlers.pdpAddToCartGeneric`;
    let transObject = params ? { key: theKey, params } : { key: theKey };
    this.globalMessage.add(transObject, GlobalMessageType.MSG_TYPE_ERROR, this.GLOBAL_MESSAGE_TIMEOUT);
    return of({
      cartModifications: [],
    });
  }

  handleGroupCheckoutErrors(errorResponse: any): Observable<GroupCheckoutStepData> {
    var params = {
      msg: '',
    };
    if (errorResponse.error && errorResponse.error.errors && errorResponse.error.errors.length) {
      params = { msg: errorResponse.error.errors[0].message };
      this.globalMessage.add(
        { key: `groupSalesCheckoutCommon.httpHandlers.genericCheckoutErr`, params },
        GlobalMessageType.MSG_TYPE_ERROR,
        this.GLOBAL_MESSAGE_TIMEOUT
      );
    }
    return of({
      cartModifications: [],
      groupOrderInfo: {},
      groupRuleData: {},
      orderCode: '',
      orderGuid: '',
    });
  }

  // Raj: USJCOMM-1631: USJCOMM-3684: START
  fetchGroupRulesCheckout(orderId: string | null): Observable<GroupCheckoutStepData> {
    var queryURL = 'udxUsers/current/group-checkout';
    if (orderId) {
      queryURL += '?orderId=' + orderId;
    }
    return this._http.get<GroupCheckoutStepData>(this._occEndpoints.buildUrl(queryURL)).pipe(
      catchError((error) => this.handleGroupCheckoutErrors(error)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  saveDraftGroupRulesCheckout(draftObject: GroupCheckoutStepData) {
    return this._http
      .post<GroupCheckoutStepData>(
        this._occEndpoints.buildUrl('groupRuleSaveDraft', {
          urlParams: {
            orderId: draftObject.orderCode,
          },
        }),
        draftObject,
        httpOptions
      )
      .pipe(catchError((error) => this.handleGroupCheckoutErrors(error)));
  }

  submitGroupOrder(draftObject: GroupCheckoutStepData) {
    let enableBEValidationObject = {
      queryParams: {
        enableBEValidations: false,
      },
    };
    this.dataTransfer.updateGroupSalesCvvCode('');

    return this._http
      .post<GroupCheckoutStepData>(
        this._occEndpoints.buildUrl('groupRuleSubmitOrder' /*, enableBEValidationObject*/),
        draftObject,
        httpOptions
      )
      .pipe(catchError((error) => this.handleGroupCheckoutErrors(error)));
  }
  // Raj: USJCOMM-1631: USJCOMM-3684: END

  getGroupSaleOrderHistory(
    sortBy: string,
    orderId: string,
    groupName: string,
    creationFrom: string,
    creationTo: string,
    ticketVisitDateFrom: string,
    ticketVisitDateTo: string,
    orderStatus: string,
    documentStatus: string,
    busParkingReservation: string,
    currentPage: string,
    pageSize: string
  ) {
    let queryParams = new HttpParams();
    if (sortBy != '' && sortBy != null) {
      queryParams = queryParams.append('sort', sortBy);
    }
    if (orderId != '' && orderId != null) {
      queryParams = queryParams.append('orderId', orderId);
    }
    if (groupName != '' && groupName != null) {
      queryParams = queryParams.append('groupName', groupName);
    }
    if (creationFrom != '' && creationFrom != null) {
      queryParams = queryParams.append('creationFrom', creationFrom);
    }
    if (creationTo != '' && creationTo != null) {
      queryParams = queryParams.append('creationTo', creationTo);
    }
    if (ticketVisitDateFrom != '' && ticketVisitDateFrom != null) {
      queryParams = queryParams.append('ticketVisitDateFrom', ticketVisitDateFrom);
    }
    if (ticketVisitDateTo != '' && ticketVisitDateTo != null) {
      queryParams = queryParams.append('ticketVisitDateTo', ticketVisitDateTo);
    }
    if (orderStatus != '' && orderStatus != null) {
      queryParams = queryParams.append('statuses', orderStatus);
    }
    if (documentStatus != '' && documentStatus != null) {
      queryParams = queryParams.append('documentStatus', documentStatus);
    }
    if (busParkingReservation != '' && busParkingReservation != null) {
      queryParams = queryParams.append('busParkingReservation', busParkingReservation);
    }
    if (currentPage != '' && currentPage != null) {
      queryParams = queryParams.append('currentPage', currentPage);
    }
    if (pageSize != '' && pageSize != null) {
      queryParams = queryParams.append('pageSize', pageSize);
    }
    let userId = '';
    this.userIdService
      .getUserId()
      .pipe(take(1))
      .subscribe((e: any) => {
        if (e) {
          userId = e;
        }
      });
    return this._http
      .get<GroupSalesOrderHistory>(
        this._occEndpoints.buildUrl('groupSalesProductOrderHistory', {
          urlParams: {
            userId: userId,
          },
        }),
        {
          params: queryParams,
        }
      )
      .pipe(
        catchError((error) => {
          let errorMessage = '';
          console.log(error.error.errors);
          if (error.error && error.error.errors && error.error.errors.length > 0) {
            errorMessage = error.error.errors[0].message;
            this.globalMessage.add(errorMessage, GlobalMessageType.MSG_TYPE_ERROR, 10000);
          } else {
            this.globalMessage.add({ key: 'httpHandlers.unknownError' }, GlobalMessageType.MSG_TYPE_ERROR, 10000);
          }
          return throwError(errorMessage);
        })
      );
  }

  performDocumentUpload(formData: FormData, draftObject: GroupCheckoutStepData, orderDetailPage = false) {
    let documentUploadAPI = orderDetailPage == false ? 'documentUpload' : 'orderDetailsDocumentUpload';
    return this._http.post<FormData>(
      this._occEndpoints.buildUrl(documentUploadAPI, {
        queryParams: {
          orderId: draftObject.orderCode,
        },
      }),
      formData
    );
  }

  deleteUploadedDocument(aMedia: DocumentDetails, orderID: string) {
    let mediaCodeID = aMedia.code;
    return this._http.delete(
      this._occEndpoints.buildUrl('removeGroupDocument', {
        queryParams: {
          mediaCodes: mediaCodeID,
          orderId: orderID,
        },
      })
    );
  }

  protected getGroupCheckoutType(groupRuleObject: GroupCheckoutStepData): GroupCheckoutType {
    var rulesProductType: GroupCheckoutRulesProductType = GroupCheckoutRulesProductType.eRulesProductTypeNone;
    if (groupRuleObject.groupRuleData?.rulesProductType?.key?.toUpperCase() === 'School'.toUpperCase()) {
      rulesProductType = rulesProductType | GroupCheckoutRulesProductType.eRulesProductTypeSchool;
    }
    if (groupRuleObject.groupRuleData?.rulesProductType?.key?.toUpperCase() === 'North America'.toUpperCase()) {
      rulesProductType = rulesProductType | GroupCheckoutRulesProductType.eRulesProductTypeSchool;
    }
    if (groupRuleObject.groupRuleData?.rulesProductType?.key?.toUpperCase() === 'General'.toUpperCase()) {
      rulesProductType = rulesProductType | GroupCheckoutRulesProductType.eRulesProductTypeGeneral;
    }

    var customerBaseType: GroupCheckoutCustomerBaseType = GroupCheckoutCustomerBaseType.eCustomerBaseTypeNone;
    if (groupRuleObject.groupRuleData?.customerBase?.key?.toUpperCase() === 'Domestic'.toUpperCase()) {
      customerBaseType = customerBaseType | GroupCheckoutCustomerBaseType.eCustomerBaseTypeDomestic;
    }
    if (groupRuleObject.groupRuleData?.customerBase?.key?.toUpperCase() === 'Inbound'.toUpperCase()) {
      customerBaseType = customerBaseType | GroupCheckoutCustomerBaseType.eCustomerBaseTypeInbound;
    }
    return {
      eGroupCheckoutRulesProductType: rulesProductType,
      eGroupCheckoutCustomerBaseType: customerBaseType,
    };
  }

  isDomesticGeneral(groupRuleObject: GroupCheckoutStepData): boolean {
    let groupRuleType = this.getGroupCheckoutType(groupRuleObject);
    var returnValue = false;
    if (
      groupRuleType.eGroupCheckoutCustomerBaseType! & GroupCheckoutCustomerBaseType.eCustomerBaseTypeDomestic &&
      groupRuleType.eGroupCheckoutRulesProductType! & GroupCheckoutRulesProductType.eRulesProductTypeGeneral
    ) {
      returnValue = true;
    }
    return returnValue;
  }
  isDomesticSchool(groupRuleObject: GroupCheckoutStepData): boolean {
    let groupRuleType = this.getGroupCheckoutType(groupRuleObject);
    var returnValue = false;
    if (
      groupRuleType.eGroupCheckoutCustomerBaseType! & GroupCheckoutCustomerBaseType.eCustomerBaseTypeDomestic &&
      groupRuleType.eGroupCheckoutRulesProductType! & GroupCheckoutRulesProductType.eRulesProductTypeSchool
    ) {
      returnValue = true;
    }
    return returnValue;
  }
  isInboundGeneral(groupRuleObject: GroupCheckoutStepData): boolean {
    let groupRuleType = this.getGroupCheckoutType(groupRuleObject);
    var returnValue = false;
    if (
      groupRuleType.eGroupCheckoutCustomerBaseType! & GroupCheckoutCustomerBaseType.eCustomerBaseTypeInbound &&
      groupRuleType.eGroupCheckoutRulesProductType! & GroupCheckoutRulesProductType.eRulesProductTypeGeneral
    ) {
      returnValue = true;
    }
    return returnValue;
  }
  isInboundSchool(groupRuleObject: GroupCheckoutStepData): boolean {
    let groupRuleType = this.getGroupCheckoutType(groupRuleObject);
    var returnValue = false;
    if (
      groupRuleType.eGroupCheckoutCustomerBaseType! & GroupCheckoutCustomerBaseType.eCustomerBaseTypeInbound &&
      groupRuleType.eGroupCheckoutRulesProductType! & GroupCheckoutRulesProductType.eRulesProductTypeSchool
    ) {
      returnValue = true;
    }
    return returnValue;
  }

  getOrderDetails(orderNumber: string): Observable<{ order?: Order; error?: any }> {
    let userId$ = this.userIdService.takeUserId();
    return userId$.pipe(
      map((res) => {
        var paramsObject: DynamicAttributes = {
          urlParams: {
            userId: res,
            orderCode: orderNumber,
          },
        };
        return paramsObject;
      }),
      switchMap((paramsObject) => {
        // return this._http.get<Order>('assets/groupSalesOrderDetails.json');
        return this._http.get<Order>(this._occEndpoints.buildUrl('groupOrderDetails', paramsObject), httpOptions).pipe(
          map((order) => ({ order } as { order: Order })), // Wrap the successful order in a consistent structure
          catchError((error) => {
            this.handleGroupOrderDetails(error);
            return of({ error });
          })
        );
      })
    );
  }

  postAdditionalOrder(additionalOrderPayload: AdditionalOrderPOSTPayload, inOrderID: string) {
    let userId$ = this.userIdService.takeUserId();
    return userId$.pipe(
      map((res) => {
        var paramsObject: DynamicAttributes = {
          urlParams: {
            userId: res,
          },
          queryParams: {
            orderId: inOrderID,
          },
        };
        return paramsObject;
      }),
      switchMap((paramsObject) => {
        return this._http.post<any>(
          this._occEndpoints.buildUrl('postAdditionalOrderEndpoint', paramsObject),
          additionalOrderPayload,
          httpOptions
        );
      })
    );
  }

  updateRemarksContent(draftObject: GroupCheckoutStepData) {
    let orderId = draftObject.orderCode;
    let postBody = {
      orderId: orderId,
      remarks: draftObject.groupOrderInfo!.remarks,
    };
    let userId$ = this.userIdService.takeUserId();
    return userId$.pipe(
      map((res) => {
        var paramsObject: DynamicAttributes = {
          urlParams: {
            userId: res,
          },
        };
        return paramsObject;
      }),
      switchMap((paramsObject) => {
        return this._http.post<any>(this._occEndpoints.buildUrl('updateRemarks', paramsObject), postBody, httpOptions);
      })
    );
  }

  handleGroupOrderDetails(errorResponse: any): Observable<Order> {
    var params = {
      msg: '',
    };
    if (errorResponse.error && errorResponse.error.errors.length) {
      params = { msg: errorResponse.error.errors[0].message };
    }
    this.globalMessage.add(
      { key: `groupSalesOrderDetails.httpHandlers.error`, params },
      GlobalMessageType.MSG_TYPE_ERROR,
      this.GLOBAL_MESSAGE_TIMEOUT
    );
    return of();
  }

  downloadParkingTicket(orderCode: string): Observable<MediaData> {
    let userId$ = this.userIdService.takeUserId();
    let dialogRef: MatDialogRef<ProgressSpinnerDialogComponent> = this.dialog.open(ProgressSpinnerDialogComponent, {
      panelClass: 'transparent',
      disableClose: true,
    });
    return userId$.pipe(
      map((res) => {
        var paramsObject: DynamicAttributes = {
          urlParams: {
            userId: res,
            orderCode: orderCode,
          },
        };
        return paramsObject;
      }),
      switchMap((paramsObject) => {
        return this._http
          .get<MediaData>(this._occEndpoints.buildUrl('downloadParkingTicket', paramsObject), httpOptions)
          .pipe(
            tap((res) => {
              dialogRef.close();
              if (!res.downloadUrl) {
                this.globalMessage.add(
                  { key: `groupSalesOrderDetails.download_error` },
                  GlobalMessageType.MSG_TYPE_ERROR
                );
              } else {
                let origin: string = this._occEndpoints.getBaseUrl({
                  baseUrl: true,
                  prefix: false,
                  baseSite: false,
                });
                this.globalMessage.add(
                  { key: `groupSalesOrderDetails.download_msg` },
                  GlobalMessageType.MSG_TYPE_CONFIRMATION,
                  this.GLOBAL_MESSAGE_TIMEOUT
                );
                window.open(origin + res.downloadUrl, '_blank');
              }
            })
          );
      })
    );
  }
  getRefundOrderDetails(
    orderNumber: string,
    sortSelectedOption: string,
    ticketTypeSelectedOption: string,
    redeemedKey: string,
    ticketIdInputs: string,
    refundAll = false,
    ticketCodes: any[] = [],
    currentPage?: string,
    pageSize?: string
  ): Observable<UDXGroupSalesRefundData> {
    let queryParams = new HttpParams();
    if (sortSelectedOption != '' && sortSelectedOption != null) {
      queryParams = queryParams.append('sort', sortSelectedOption);
    }
    if (ticketTypeSelectedOption != '' && ticketTypeSelectedOption != null) {
      queryParams = queryParams.append('productCode', ticketTypeSelectedOption);
    }
    if (redeemedKey != '' && redeemedKey != null) {
      let redeemed = redeemedKey == 'code-yes' ? true : redeemedKey == 'code-no' ? false : undefined;
      if (redeemed != undefined) {
        queryParams = queryParams.append('redeemed', redeemed);
      } else {
        console.log('redeemed cannot be undefined!');
      }
    }
    if (ticketIdInputs != '' && ticketIdInputs != null) {
      queryParams = queryParams.append('ticketCodes', ticketIdInputs);
    }
    if (currentPage != '' && currentPage != null) {
      queryParams = queryParams.append('currentPage', currentPage);
    }
    if (pageSize != '' && pageSize != null) {
      queryParams = queryParams.append('pageSize', pageSize);
    }
    if (refundAll) {
      queryParams = queryParams.append('refundAll', refundAll);
    }
    if (ticketCodes.length) {
      queryParams = queryParams.append('ticketCodes', ticketCodes.toString());
    }
    let userId$ = this.userIdService.takeUserId();
    return userId$.pipe(
      map((res) => {
        var paramsObject: DynamicAttributes = {
          urlParams: {
            userId: res,
            orderCode: orderNumber,
          },
        };
        return paramsObject;
      }),
      switchMap((paramsObject) => {
        // return this._http.get<Order>('assets/groupSalesOrderDetails.json');
        return this._http
          .get<UDXGroupSalesRefundData>(this._occEndpoints.buildUrl('refundGroupOrder', paramsObject), {
            params: queryParams,
          })
          .pipe(catchError((error) => this.handleGroupOrderRefundDetails(error)));
      })
    );
  }
  groupSalesVoidOrder(orderId: string): Observable<any> {
    let userId$ = this.userIdService.takeUserId();
    return userId$.pipe(
      map((res) => {
        var paramsObject: DynamicAttributes = {
          urlParams: {
            userId: res,
            orderCode: orderId,
          },
        };
        return paramsObject;
      }),
      switchMap((paramsObject) => {
        return this._http.post(this._occEndpoints.buildUrl('voidGroupOrder', paramsObject), {}, { observe: 'response' })
      })
    );
  }

  handleGroupOrderRefundDetails(errorResponse: any): Observable<UDXGroupSalesRefundData> {
    var params = {
      msg: '',
    };
    if (errorResponse.error && errorResponse.error.errors.length) {
      params = { msg: errorResponse.error.errors[0].message };
    }
    this.globalMessage.add(
      { key: `groupSalesOrderDetails.httpHandlers.error`, params },
      GlobalMessageType.MSG_TYPE_ERROR,
      this.GLOBAL_MESSAGE_TIMEOUT
    );
    return of();
  }

  resetSteps(): void {
    this.allSteps.map((GroupCheckoutStep) => Object.assign({}, GroupCheckoutStep));
    this.steps$.next(this.allSteps);
  }

  disableEnableStep(currentStepID: string, disabled: boolean): void {
    const currentStep = this.filteredSteps.find((step) =>
      step.id.includes(currentStepID ? currentStepID : currentStepID.toLowerCase())
    );

    this.filteredSteps = this.filteredSteps.filter((step) => step.id !== currentStepID);
    if (currentStep && disabled) {
      this.steps$.next(this.filteredSteps.filter((step) => step.id !== currentStep.id));
    }
  }
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

const httpOptionsResponseAcceptText = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'text/plain',
  }),
};
